import { useEffect, useRef, useState } from "react";
import {
  ArrowDownNarrowWide,
  ArrowUpNarrowWide,
  ChevronsUpDown,
  Clock,
  FileText,
  FoldVertical,
  Search,
  SlidersHorizontal,
  UnfoldVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import RecordNew from "./RecordNew";
import { capitalize } from "@/common/utils";
import { getPlural } from "@/common/intervalTypes";
import { HGInterval, HGIntervalType } from "@/common/types";
import {
  autoGroupBy,
  groupByOptions,
  thenByOptions,
  resolveLevelTypes,
} from "@/common/recordTree";
import { RecordsViewOptions } from "@/state/recordsView";
import { useElementWidth } from "@/common/hooks";

interface RecordsToolbarProps {
  options: RecordsViewOptions;
  setOption: <K extends keyof RecordsViewOptions>(
    key: K,
    value: RecordsViewOptions[K],
  ) => void;
  /** Free-text filter over the currently listed records. */
  query: string;
  setQuery: (value: string) => void;
  interval: HGInterval;
  endInterval: HGInterval | null;
  /**
   * Whether notes are actually being shown. Owned by Records so the toolbar and
   * the body can never disagree: `options.mode` alone is not enough, since a
   * stored "notes" mode falls back to the timeline outside a collection.
   */
  notesMode: boolean;
  /** Notes live in a collection, so the mode switch only exists inside one. */
  showModeSwitch: boolean;
  onCollapseAll: () => void;
  onExpandAll: () => void;
}

// "children" is still a valid scope everywhere else — it is only kept out of
// the toolbar for now, so the switch offers the two ends of the range.
const SCOPE_CHOICES = ["direct", "all"] as const;

const SCOPE_LABELS = {
  direct: "Direct",
  children: "Children",
  all: "All",
} as const;

const SCOPE_TITLES = {
  direct: "Only this collection's own records",
  children: "This collection and its immediate subcollections",
  all: "Every nested collection, at any depth",
} as const;

const triggerClass =
  "h-7 px-2 gap-1 font-normal text-[12.5px] text-ink2 hover:bg-row-hover data-[state=open]:bg-row-hover";

const iconButtonClass =
  "w-[30px] h-[30px] rounded-[6px] text-muted-foreground hover:bg-row-hover hover:text-foreground";

function typeLabel(it: HGIntervalType) {
  return capitalize(getPlural(it));
}

export default function RecordsToolbar({
  options,
  setOption,
  query,
  setQuery,
  interval,
  endInterval,
  notesMode,
  showModeSwitch,
  onCollapseAll,
  onExpandAll,
}: RecordsToolbarProps) {
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  // Fold the grouping/order controls into the display menu once the toolbar
  // itself — not the viewport — gets too narrow to hold them inline.
  const inlineControls = width >= 720;
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery("");
  };

  const groupOptions = groupByOptions(interval, endInterval);
  const resolved = resolveLevelTypes(interval, endInterval, options);
  const autoGroup = autoGroupBy(interval, endInterval);
  const thenOptions = resolved[0] ? thenByOptions(resolved[0]) : [];

  const groupLabel = resolved[0] ? typeLabel(resolved[0]) : "—";
  const thenLabel =
    options.thenBy === "none"
      ? "None"
      : resolved[1]
        ? typeLabel(resolved[1])
        : "—";

  const autoGroupLabel = autoGroup ? `Auto (${getPlural(autoGroup)})` : "Auto";

  const groupByMenu = (
    <DropdownMenuRadioGroup
      value={options.groupBy}
      onValueChange={(value) =>
        setOption("groupBy", value as RecordsViewOptions["groupBy"])
      }
    >
      <DropdownMenuRadioItem value="auto" className="text-[13px]">
        {autoGroupLabel}
      </DropdownMenuRadioItem>
      {groupOptions.map((it) => (
        <DropdownMenuRadioItem key={it} value={it} className="text-[13px]">
          {typeLabel(it)}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );

  const thenByMenu = (
    <DropdownMenuRadioGroup
      value={options.thenBy}
      onValueChange={(value) =>
        setOption("thenBy", value as RecordsViewOptions["thenBy"])
      }
    >
      <DropdownMenuRadioItem value="auto" className="text-[13px]">
        Auto
      </DropdownMenuRadioItem>
      <DropdownMenuRadioItem value="none" className="text-[13px]">
        None
      </DropdownMenuRadioItem>
      {thenOptions.map((it) => (
        <DropdownMenuRadioItem key={it} value={it} className="text-[13px]">
          {typeLabel(it)}
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );

  const toggleOrder = () =>
    setOption("order", options.order === "asc" ? "desc" : "asc");

  const OrderIcon =
    options.order === "asc" ? ArrowDownNarrowWide : ArrowUpNarrowWide;

  return (
    <div
      ref={rootRef}
      className="flex h-[44px] min-w-0 shrink-0 items-center gap-2 overflow-x-auto border-b border-border bg-background px-3 [&::-webkit-scrollbar]:hidden"
    >
      {showModeSwitch && (
        <div className="flex shrink-0 gap-[3px] rounded-md border border-border bg-card p-[3px]">
          {(["time", "notes"] as const).map((mode) => {
            const active = mode === "notes" ? notesMode : !notesMode;
            const Icon = mode === "time" ? Clock : FileText;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => setOption("mode", mode)}
                className={`flex h-[24px] items-center gap-1 rounded-[5px] px-2 text-[12.5px] transition-colors ${
                  active
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-muted-foreground hover:bg-row-hover hover:text-foreground"
                }`}
              >
                <Icon className="size-3.5" />
                {mode === "time" ? "Time" : "Notes"}
              </button>
            );
          })}
        </div>
      )}

      {showModeSwitch && !notesMode && (
        <div className="flex shrink-0 gap-[3px] rounded-md border border-border bg-card p-[3px]">
          {SCOPE_CHOICES.map((scope) => {
            const active = options.collectionScope === scope;
            return (
              <button
                key={scope}
                type="button"
                title={SCOPE_TITLES[scope]}
                onClick={() => setOption("collectionScope", scope)}
                className={`flex h-[24px] items-center rounded-[5px] px-2 text-[12.5px] transition-colors ${
                  active
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "text-muted-foreground hover:bg-row-hover hover:text-foreground"
                }`}
              >
                {SCOPE_LABELS[scope]}
              </button>
            );
          })}
        </div>
      )}

      {/* Grouping and order live inline when there's room, and fold into the
          display menu when there isn't. */}
      {!notesMode && inlineControls && (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={triggerClass}>
                <span className="text-muted-foreground">Group</span>
                <span className="truncate">{groupLabel}</span>
                <ChevronsUpDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {groupByMenu}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={triggerClass}>
                <span className="text-muted-foreground">Then</span>
                <span className="truncate">{thenLabel}</span>
                <ChevronsUpDown className="size-3.5 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-44">
              {thenByMenu}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title={options.order === "asc" ? "Oldest first" : "Newest first"}
            onClick={toggleOrder}
          >
            <OrderIcon className="size-[15px]" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title="Collapse all"
            onClick={onCollapseAll}
          >
            <FoldVertical className="size-[15px]" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={iconButtonClass}
            title="Expand all"
            onClick={onExpandAll}
          >
            <UnfoldVertical className="size-[15px]" />
          </Button>
        </>
      )}

      {notesMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className={triggerClass}>
              <span className="text-muted-foreground">Sort</span>
              <span className="truncate">
                {options.notesSort === "created" ? "Created" : "Modified"}
              </span>
              <ChevronsUpDown className="size-3.5 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-44">
            <DropdownMenuRadioGroup
              value={options.notesSort}
              onValueChange={(value) =>
                setOption("notesSort", value as RecordsViewOptions["notesSort"])
              }
            >
              <DropdownMenuRadioItem value="created" className="text-[13px]">
                Created
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="modified" className="text-[13px]">
                Modified
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={options.notesOrder}
              onValueChange={(value) =>
                setOption(
                  "notesOrder",
                  value as RecordsViewOptions["notesOrder"],
                )
              }
            >
              <DropdownMenuRadioItem value="desc" className="text-[13px]">
                Newest first
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="asc" className="text-[13px]">
                Oldest first
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <div className="flex-1" />

      {searchOpen ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") closeSearch();
            }}
            onBlur={() => {
              if (!query) setSearchOpen(false);
            }}
            placeholder="Search records..."
            className="h-7 w-40 pl-7 text-[13px] md:w-56"
          />
        </div>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className={iconButtonClass}
          title="Search records"
          onClick={() => setSearchOpen(true)}
        >
          <Search className="size-[15px]" />
        </Button>
      )}

      {!notesMode && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={iconButtonClass}
              title="Display options"
            >
              <SlidersHorizontal className="size-[15px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-56">
            {!inlineControls && (
              <>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-[13px]">
                    Group by · {groupLabel}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>{groupByMenu}</DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-[13px]">
                    Then by · {thenLabel}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>{thenByMenu}</DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="text-[13px]">
                    Order
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={options.order}
                      onValueChange={(value) =>
                        setOption("order", value as RecordsViewOptions["order"])
                      }
                    >
                      <DropdownMenuRadioItem
                        value="asc"
                        className="text-[13px]"
                      >
                        Oldest first
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem
                        value="desc"
                        className="text-[13px]"
                      >
                        Newest first
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuCheckboxItem
              className="text-[13px]"
              checked={options.showGaps}
              onCheckedChange={(checked) => setOption("showGaps", !!checked)}
            >
              Show gaps
            </DropdownMenuCheckboxItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
              Records spanning several intervals
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={options.spanning}
              onValueChange={(value) =>
                setOption("spanning", value as RecordsViewOptions["spanning"])
              }
            >
              <DropdownMenuRadioItem value="ends" className="text-[13px]">
                Start &amp; end
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="first" className="text-[13px]">
                First only
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="every" className="text-[13px]">
                Every interval
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>

            {!inlineControls && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-[13px]"
                  onSelect={onCollapseAll}
                >
                  Collapse all
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-[13px]"
                  onSelect={onExpandAll}
                >
                  Expand all
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <RecordNew mode={notesMode ? "notes" : "time"} />
    </div>
  );
}
